import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  /*
   * Serverside Environment variables, not available on the client.
   * Will throw if you access these variables on the client.
   */
  server: {
    NODE_ENV: z.string().optional(),
    CONTENTFUL_ENDPOINT: z.string().nonempty(),
    CONTENTFUL_ACCESS_TOKEN: z.string().nonempty(),
    CONTENTFUL_PREVIEW: z
      .boolean()
      .describe(
        "By default, Contentful queries do not return draft content. If you would like to override this behaviour, enable this flag. Needs to be used in conjunction with a CONTENTFUL_ACCESS_TOKEN with preview access.",
      ),
    CANONICAL_URL_HOST: z.string().nonempty().default("www.redbull.com"),
    CREPO_GRAPHQL_ENDPOINT: z.string().nonempty(),
    CREPO_GRAPHQL_API_KEY: z.string().nonempty(),
    STORYBLOK_PREVIEW_ACCESS_TOKEN: z
      .string()
      .nonempty()
      .describe(
        "Retrieved from: https://app.storyblok.com/#/me/spaces/287059/settings?tab=api",
      ),
    STORYBLOK_DRAFTS_ENABLED: z.stringbool().prefault("false"),
    STORYBLOK_STAGING_ENABLED: z.stringbool().prefault("false"),
    OFFICERAIL_STORYBLOK_ACCESS_TOKEN: z
      .string()
      .optional()
      .describe(
        "Public token of the Red Bull Jobs Storyblok space, read at build time by src/generate-office-locations.mjs for the office rail on the company page. Optional: when unset, the build keeps the committed src/__generated__/office-locations.json.",
      ),
    VALKEY_STORY_CACHE_ENABLED: z
      .stringbool()
      .prefault("false")
      .describe(
        "Enables the Storyblok story cache (src/helpers/storyblokStoryCache.ts). Independent of STORYBLOK_DRAFTS_ENABLED, so it can stay on even while previewing drafts. See docs/valkey-cache.md.",
      ),
    VALKEY_STORY_CACHE_WEBHOOK_SECRET: z
      .string()
      .optional()
      .describe(
        "The secret configured on the Storyblok webhook (Settings → Webhooks) that points at /api/storyblok-webhook. Every request to that route is verified against it before the Storyblok story cache is flushed. Optional so the app boots without it, but the route then accepts nothing and answers 503 — an unsigned request must never be able to flush the cache. See docs/valkey-cache.md.",
      ),
    PCS_PRODUCT_RAIL_API_KEY: z
      .string()
      .nonempty()
      .transform((value) =>
        value
          .split(",")
          .map((key) => key.trim())
          .filter(Boolean),
      )
      .pipe(z.array(z.string().min(1)).min(1))
      .describe(
        "Bearer token for GET /int-en/energydrink/api/product-rail-all, product-rail-all-drafts and product-rail-all/openapi.json. The payload lists every Storyblok-launched locale. Trusted Red Bull backends send `Authorization: Bearer <key>`. Comma-separated if a second key is ever needed for rotation.",
      ),

    /**
     * On staging (but not in production) we want to expose the internal
     * `/storyblok/` prefixed routes, so that EXA can preview their work on
     * routes that are otherwise resolved to `/contentful/` paths via rewrites,
     * such as sustainability pages and company pages which are on a different
     * release schedule to each locale's product pages.
     */
    CONTENTFUL_PREFIXED_ROUTES_ENABLED: z.stringbool().prefault("false"),
    TYPESENSE_ADMIN_API_KEY: z.string().nonempty(),
    TYPESENSE_SEARCH_ONLY_API_KEY: z.string().nonempty(),
    TYPESENSE_NODE: z.string().nonempty(),
    TYPESENSE_SEARCH_DEBUG_ENABLED: z
      .stringbool()
      .prefault("false")
      .describe("When true, enable debug page route for Typesense search."),
    VALKEY_URL: z
      .string()
      .optional()
      .describe(
        "Connection string for the shared Valkey (Redis-compatible) cache used by the Next.js cacheHandler across horizontally-scaled instances (see cache-handler.mjs / docs/valkey-cache.md). Optional: when unset the cache handler fails soft to no caching.",
      ),
    /*
     * The three story-cache lifetimes below parse their own string rather than
     * using `z.coerce.number()`, because `Number("")` is `0` and an empty
     * assignment (`VALKEY_STORY_TTL=`) is how `.sample.env` spells "not set"
     * — coercing would silently mean a zero TTL instead of the default. The
     * `.pipe()` then bounds whatever was parsed, so a typo fails the build
     * rather than caching for NaN seconds.
     */
    VALKEY_STORY_TTL: z
      .string()
      .optional()
      .transform((value) => (value ? Number(value) : 60 * 60))
      .pipe(z.number().int().positive())
      .describe(
        "How long (in seconds) a cached Storyblok story body is reused before it is re-fetched from the CDN. Story bodies are the entries that carry content editors expect to see change, so this is the shorter of the two story-cache lifetimes.",
      ),
    VALKEY_COLLECTION_TTL: z
      .string()
      .optional()
      .transform((value) => (value ? Number(value) : 12 * 60 * 60))
      .pipe(z.number().int().positive())
      .describe(
        "How long (in seconds) a cached collection's membership is reused. Deliberately far longer than VALKEY_STORY_TTL: the entry is small, its membership changes only when a story is added or removed, and rebuilding it means paging the whole collection out of the CDN (~160 requests for the largest ones).",
      ),
    VALKEY_STORY_TTL_JITTER: z
      .string()
      .optional()
      .transform((value) => (value ? Number(value) : 0.2))
      .pipe(z.number().min(0).max(1))
      .describe(
        "Fraction of VALKEY_STORY_TTL to spread each story's expiry over, so that stories written together (a collection page-through primes thousands at once) do not all expire in the same second. 0.2 gives each entry VALKEY_STORY_TTL ±20%; 0 disables jitter.",
      ),
  },
  /*
   * Environment variables available on the client (and server).
   *
   * 💡 You'll get type errors if these are not prefixed with NEXT_PUBLIC_.
   */
  client: {
    NEXT_PUBLIC_HAS_GTM_CONTAINER: z
      .boolean()
      .describe(
        "This should be “true” in environments where GTM should be enabled (ie. non-development environments)",
      ),
    NEXT_PUBLIC_ZENDESK_ENV: z
      .enum(["production", "staging", "development"])
      .default("development"),
  },
  /*
   * Due to how Next.js bundles environment variables on Edge and Client,
   * we need to manually destructure them to make sure all are included in bundle.
   *
   * 💡 You'll get type errors if not all variables from `server` & `client` are included here.
   */
  runtimeEnv: {
    /* eslint-disable no-restricted-properties */
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_HAS_GTM_CONTAINER:
      process.env.NEXT_PUBLIC_HAS_GTM_CONTAINER === "true",
    CONTENTFUL_ENDPOINT: process.env.CONTENTFUL_ENDPOINT,
    CONTENTFUL_ACCESS_TOKEN: process.env.CONTENTFUL_ACCESS_TOKEN,
    CONTENTFUL_PREVIEW: process.env.CONTENTFUL_PREVIEW === "true",
    CANONICAL_URL_HOST: process.env.CANONICAL_URL_HOST,
    CREPO_GRAPHQL_ENDPOINT: process.env.CREPO_GRAPHQL_ENDPOINT,
    CREPO_GRAPHQL_API_KEY: process.env.CREPO_GRAPHQL_API_KEY,
    STORYBLOK_PREVIEW_ACCESS_TOKEN: process.env.STORYBLOK_PREVIEW_ACCESS_TOKEN,
    STORYBLOK_DRAFTS_ENABLED: process.env.STORYBLOK_DRAFTS_ENABLED,
    STORYBLOK_STAGING_ENABLED: process.env.STORYBLOK_STAGING_ENABLED,
    OFFICERAIL_STORYBLOK_ACCESS_TOKEN:
      process.env.OFFICERAIL_STORYBLOK_ACCESS_TOKEN,
    VALKEY_STORY_CACHE_ENABLED: process.env.VALKEY_STORY_CACHE_ENABLED,
    VALKEY_STORY_CACHE_WEBHOOK_SECRET:
      process.env.VALKEY_STORY_CACHE_WEBHOOK_SECRET,
    PCS_PRODUCT_RAIL_API_KEY: process.env.PCS_PRODUCT_RAIL_API_KEY,
    NEXT_PUBLIC_ZENDESK_ENV: process.env.NEXT_PUBLIC_ZENDESK_ENV,
    CONTENTFUL_PREFIXED_ROUTES_ENABLED:
      process.env.CONTENTFUL_PREFIXED_ROUTES_ENABLED,
    TYPESENSE_ADMIN_API_KEY: process.env.TYPESENSE_ADMIN_API_KEY,
    TYPESENSE_SEARCH_ONLY_API_KEY: process.env.TYPESENSE_SEARCH_ONLY_API_KEY,
    TYPESENSE_NODE: process.env.TYPESENSE_NODE,
    TYPESENSE_SEARCH_DEBUG_ENABLED: process.env.TYPESENSE_SEARCH_DEBUG_ENABLED,
    VALKEY_URL: process.env.VALKEY_URL,
    VALKEY_STORY_TTL: process.env.VALKEY_STORY_TTL,
    VALKEY_COLLECTION_TTL: process.env.VALKEY_COLLECTION_TTL,
    VALKEY_STORY_TTL_JITTER: process.env.VALKEY_STORY_TTL_JITTER,
    /* eslint-enable no-restricted-properties */
  },
});
